import { styled } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { LoanApplicationOfferResponse, PayloadRequestPatchOffer } from "@interfaces/MarketPlace";
import { formatCurrency } from "@helpers/funciones";
import { Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import { usePatchOferSelectedMutation } from "@api/marketPlaceApis";
import { usePostValidateNextStageMutation } from "@api/applications";
import { useState } from "react";
import { ApiError } from "@interfaces/index";
import * as Sentry from "@sentry/react";
import { setLoanStatus } from "@store/slices/registerSlice";
import { setSelectedOffer } from "@store/slices/marketPlaceSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";

const BORDER_COLOR = "#A3D4E8";

const StyledTableCell = styled(TableCell)(() => ({
  border: `1px solid ${BORDER_COLOR}`,
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#052649",
    color: "#fff",
    fontWeight: 500,
    border: `1px solid ${BORDER_COLOR}`,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 15,
    color: "#002652",
    border: `1px solid ${BORDER_COLOR}`,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#fff", // Filas impares blancas
  },
  "&:nth-of-type(even)": {
    backgroundColor: "#E3F2F8", // Filas pares color claro
  },
  "&:last-child td, &:last-child th": {
    border: `1px solid ${BORDER_COLOR}`, // mantiene borde inferior
  },
}));

interface TableCreditOfferProps {
  offer: LoanApplicationOfferResponse[];
}

const NEXT_SATATUS = "COFAC";

export default function TablaOfertas({ offer }: TableCreditOfferProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const [patchOfferSelected] = usePatchOferSelectedMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const selectedOffer = async (offer: LoanApplicationOfferResponse) => {
    try {
      setIsLoading(true);
      const data: PayloadRequestPatchOffer = { accept: true };

      await patchOfferSelected({ id_offer: offer.id, id_sol: application.id, data }).unwrap();

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: NEXT_SATATUS,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      dispatch(setLoanStatus(NEXT_SATATUS));
      dispatch(setSelectedOffer(offer));
      navigate("/producto/creditosimple/oferta_aceptada");
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: { component: "Ofertaa-initial_fetch" },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TableContainer component={Paper}>
      <Table sx={{ minWidth: 700 }} aria-label="customized table">
        <TableHead>
          <TableRow>
            <StyledTableCell align="center">Entidad Financiera</StyledTableCell>
            <StyledTableCell align="center">Monto</StyledTableCell>
            <StyledTableCell align="center">Plazo Maximo</StyledTableCell>
            <StyledTableCell align="center">Tasa</StyledTableCell>
            <StyledTableCell align="center">Comisiones</StyledTableCell>
            <StyledTableCell align="center">Garantía</StyledTableCell>
            <StyledTableCell align="center">Seleccionar</StyledTableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {offer.map((row) => (
            <StyledTableRow key={row.id}>
              <StyledTableCell component="th" scope="row">
                {row.financial_company.name}
              </StyledTableCell>
              <StyledTableCell align="center">
                {formatCurrency(row.loan_amount || 0)} <span style={{ fontSize: 11 }}>MXN</span>
              </StyledTableCell>
              <StyledTableCell align="center">{row.term} meses</StyledTableCell>
              <StyledTableCell align="center">{row.interest_rate}%</StyledTableCell>
              <StyledTableCell align="center">{row.opening_fee}%</StyledTableCell>
              <StyledTableCell align="center">N/A</StyledTableCell>
              <StyledTableCell align="center">
                <Button
                  variant="yellow"
                  sx={{ fontSize: "13px" }}
                  disabled={isLoading}
                  onClick={() => selectedOffer(row)}
                >
                  Elegir Oferta
                </Button>
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
